import { use, useEffect } from "react";
import { useQuery } from "@apollo/client/react";
import { ClientStatus, ProfileType } from "../__generated__/graphql";
import { GET_CLIENT_INFO } from "../graphql/core";
import { AuthManagerContext } from "../contexts/AuthManager";
import { ProfileSwitcherManagerContext } from "../contexts/ProfileSwitcherManager";

const CLIENT_STATUS_POLL_MS = 5000;

/**
 * While the active client profile is still awaiting activation, polls the
 * lightweight GET_CLIENT_INFO query. When the server reports a different status,
 * it refreshes the full user once so the activation banner (and the rest of the
 * app) reflect the new status; once a resting state is reached, polling stops.
 *
 * The resting state depends on the mode (mirrors the banner logic):
 *  - non-LSE: active at `Signed` — so `Pending` and `Accepted` both keep polling.
 *  - LSE:     active at `Accepted` — so only `Pending` keeps polling.
 *
 * `currentStatus` is `client.status` held in the AuthManager user state. It is
 * undefined/None for LSE clients; the trigger is a positive whitelist of waiting
 * states (never a negation), so a None status never polls.
 */
export const useClientSigningStatusPolling = (
    currentStatus?: ClientStatus | null,
    useLseCct?: boolean,
): void => {
    const { refreshUser } = use(AuthManagerContext);
    const { profileId, profileType } = use(ProfileSwitcherManagerContext);

    const isAwaitingActivation =
        currentStatus === ClientStatus.Pending ||
        (!useLseCct && currentStatus === ClientStatus.Accepted);

    const shouldPoll = profileType === ProfileType.Client && !!profileId && isAwaitingActivation;

    const { data } = useQuery(GET_CLIENT_INFO, {
        fetchPolicy: "network-only",
        variables: { clientProfileId: profileId ?? "" },
        pollInterval: shouldPoll ? CLIENT_STATUS_POLL_MS : 0,
        skip: !shouldPoll,
    });

    useEffect(() => {
        if (!shouldPoll) return;
        const polledStatus = data?.getClientInfo?.status;
        // Status changed server-side -> sync the heavy user state once.
        // refreshUser() updates the context user; if the new status is a resting
        // state, shouldPoll becomes false and the query stops itself. Otherwise
        // (e.g. non-LSE Pending -> Accepted) polling continues toward Signed.
        if (polledStatus && polledStatus !== currentStatus) {
            void refreshUser?.();
        }
    }, [data?.getClientInfo?.status, currentStatus, shouldPoll, refreshUser]);
};
